import React, { useState, useEffect, useRef } from "react";
import { retrieve_messages } from "../utils/postAI";
import { Auth } from "aws-amplify";
import { useAuthenticator } from "@aws-amplify/ui-react";
import MessageList from "./chat/MessageList";
import MessageInput from "./chat/MessageInput";
import SuggestionPills from "./chat/SuggestionPills";
import { Spinner } from "./Spinner";
import { WebSocketManager } from "../utils/websocket";
import { getUserId } from "../utils/userUtils";
import { listUserCharacters } from "../utils/characterUtils";
import { 
  checkMediaUsageLimits, 
  detectMediaTypeFromMessage,
  getUpgradeMessage
} from "../utils/mediaUsageUtils";
import UsageIndicator from "./usage/UsageIndicator";

const FileAttachmentPreview = ({ file, onRemove }) => {
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-800 max-w-fit">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 flex items-center justify-center bg-pink-500 rounded-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium text-white">{file.name}</span>
          <span className="text-xs text-gray-400">PDF</span>
        </div>
      </div>
      <button
        onClick={onRemove}
        className="p-1 hover:bg-gray-700 rounded-full transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
};

const ChatBox = ({ chatBotId, threadId, subscriptionStatus }) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [userId, setUserId] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const [wsManager, setWsManager] = useState(null);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [responseTimeoutId, setResponseTimeoutId] = useState(null);
  const [userScrolling, setUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const [userCharacters, setUserCharacters] = useState([]);
  const [showUsageIndicator, setShowUsageIndicator] = useState(true);
  const [usageLimitDialog, setUsageLimitDialog] = useState(null);

  const scrollToBottom = (force = false) => {
    if (messagesEndRef.current && (force || !userScrolling)) {
      console.log(force ? "Forcing scroll to bottom" : "Scrolling to bottom");
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    } else if (userScrolling && !force) {
      console.log("User is scrolling, not auto-scrolling to bottom");
    } else {
      console.warn("messagesEndRef.current is null, cannot scroll");
    }
  };

  useEffect(() => {
    console.log("Messages updated, scrolling to bottom");
    // Use a small timeout to ensure the DOM has updated
    // Only auto-scroll if we're at the bottom already or this is a new thread
    const shouldForceScroll = messages.length <= 1; // Force scroll for first message or empty thread
    setTimeout(() => scrollToBottom(shouldForceScroll), 100);
  }, [messages]);

  // Fetch user's custom characters
  const fetchCharacters = async (userId) => {
    try {
      const characters = await listUserCharacters(userId);
      setUserCharacters(characters);
    } catch (error) {
      console.error("Error fetching characters:", error);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    Auth.currentAuthenticatedUser()
      .then(async (user) => {
        const userId = getUserId(user);
        if (!userId) {
          throw new Error("No user ID available");
        }
        setUserId(userId);
        document.cookie = `user_id=${userId}; path=/; SameSite=None; Secure`;

        // Fetch user characters for avatar display
        await fetchCharacters(userId);

        // If we don't have a thread ID yet, don't automatically create one
        let threadToUse = threadId || currentThreadId;
        console.log(
          `Initial thread check - threadId: ${threadId}, currentThreadId: ${currentThreadId}`
        );

        if (!threadToUse) {
          console.log(
            `No thread ID available for user ${userId} and bot ${chatBotId}. Waiting for user to create a thread.`
          );
          setIsLoading(false);
          return;
        } else {
          console.log(`Using existing thread ID: ${threadToUse}`);
          setCurrentThreadId(threadToUse);
          return fetchMessages(userId, chatBotId, threadToUse);
        }
      })
      .catch((err) => console.error("Error fetching user:", err))
      .finally(() => setIsLoading(false));
  }, [chatBotId, threadId, currentThreadId]);

  useEffect(() => {
    if (userId) {
      // Only create a WebSocket manager if we have both userId and currentThreadId
      if (currentThreadId) {
        console.log(
          `Creating WebSocket manager with userId: ${userId} and threadId: ${currentThreadId}`
        );
        const manager = new WebSocketManager(userId);

        manager.connect().catch((error) => {
          console.error("Failed to connect to WebSocket:", error);
        });

        const handleMessage = (response) => {
          console.log("Received WebSocket message in ChatBox:", response);
          setIsTyping(false); // Always stop typing indicator when we get a response

          // Clear the timeout when we receive a response
          if (responseTimeoutId) {
            console.log("Clearing response timeout");
            clearTimeout(responseTimeoutId);
            setResponseTimeoutId(null);
          }

          if (response.type === "ai_response") {
            // Check if this response belongs to the current thread
            if (response.thread_id && response.thread_id !== currentThreadId) {
              console.log(
                `Ignoring message for different thread. Message thread: ${response.thread_id}, Current thread: ${currentThreadId}`
              );
              return; // Skip messages for other threads
            }

            // Check if the response contains an error message
            const isErrorMessage =
              response.answer &&
              (response.answer.startsWith("Error:") ||
                response.answer.startsWith("I'm sorry, I couldn't process"));

            const aiMessage = {
              id: `ai_${Date.now()}`,
              type: "ai",
              msg: response.answer || "",
              isError: isErrorMessage,
              thread_id: response.thread_id || currentThreadId, // Store thread_id with the message
            };

            console.log(
              `Adding AI message to chat thread ${currentThreadId}:`,
              aiMessage
            );

            // Use a callback function to ensure we're working with the latest state
            // Only add the message if it has content (not null, undefined, or empty string)
            if (aiMessage.msg && aiMessage.msg !== "") {
              setMessages((prevMessages) => {
                console.log("Previous messages:", prevMessages);
                const newMessages = [...prevMessages, aiMessage];
                console.log("New messages array:", newMessages);
                return newMessages;
              });
            } else {
              console.log("Skipping empty AI message with id:", aiMessage.id);
            }

            // Force a scroll after the state update
            setTimeout(() => {
              console.log("Forcing scroll to bottom after message update");
              scrollToBottom(true); // Force scroll for AI responses
            }, 100);

            // If this was an error message, log it for debugging
            if (isErrorMessage) {
              console.error(
                "Received error response from AI:",
                response.answer
              );
            }
          } else {
            console.warn("Received unknown message type:", response);
          }
        };

        manager.addMessageHandler(handleMessage);
        setWsManager(manager);

        return () => {
          manager.removeMessageHandler(handleMessage);
          manager.disconnect();
        };
      } else {
        console.warn(
          "Not creating WebSocket manager because currentThreadId is not available"
        );
      }
    }
  }, [userId, currentThreadId]); // Removed responseTimeoutId to prevent unnecessary reconnections

  // Add a separate effect to handle response timeout cleanup
  useEffect(() => {
    // This effect will clean up the timeout when responseTimeoutId changes or component unmounts
    return () => {
      if (responseTimeoutId) {
        console.log("Cleaning up response timeout on effect cleanup");
        clearTimeout(responseTimeoutId);
      }
    };
  }, [responseTimeoutId]);

  // Add cleanup for scroll timeout
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        console.log("Cleaning up scroll timeout on component unmount");
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (threadId) {
      console.log(`Thread ID changed to: ${threadId}`);
      setCurrentThreadId(threadId);
      // Fetch messages for this specific thread if we have a userId
      if (userId) {
        fetchMessages(userId, chatBotId, threadId);
      }

      // Save this as the last visited thread
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(
          "lastVisitedThread",
          JSON.stringify({ botId: chatBotId, threadId })
        );
        console.log(
          `Saved last visited thread from ChatBox: botId=${chatBotId}, threadId=${threadId}`
        );
      }
    }
  }, [threadId, userId, chatBotId]);

  const fetchMessages = async (userId, chatBotId, threadId) => {
    if (!threadId) {
      console.error("Thread ID is required for fetching messages");
      return;
    }

    console.log(
      `Fetching messages for thread ${threadId}, user ${userId}, bot ${chatBotId}`
    );

    try {
      const retrievedMessages = await retrieve_messages(
        userId,
        10,
        chatBotId,
        threadId
      );

      console.log(
        `Retrieved ${retrievedMessages.length} messages:`,
        retrievedMessages
      );

      // Check if we got a valid response
      if (Array.isArray(retrievedMessages) && retrievedMessages.length > 0) {
        // Check if the first message is empty (which indicates no messages found)
        const firstMessage = retrievedMessages[0];
        if (
          firstMessage &&
          firstMessage.id === 0 &&
          firstMessage.type === "" &&
          firstMessage.msg === ""
        ) {
          console.log(
            "Received empty message array from backend, setting empty messages array"
          );
          setMessages([]);
        } else {
          setMessages(retrievedMessages);
        }
      } else {
        console.log(
          "No messages retrieved or invalid response, setting empty messages array"
        );
        setMessages([]);
      }
    } catch (error) {
      console.error("Error retrieving messages:", error);
      setMessages([]);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handlePostRequest();
    }
  };

  const handlePostRequest = async () => {
    if (!input.trim() && !attachedFile) return;

    // Check subscription status before allowing message sending
    if (subscriptionStatus && !subscriptionStatus.canSendMessages) {
      console.log('Message sending blocked due to subscription status:', subscriptionStatus);
      
      // Add a system message explaining the restriction
      const restrictionMessage = {
        id: `system_${Date.now()}`,
        type: "ai",
        msg: `${subscriptionStatus.message}. Please subscribe to send messages to this character.`,
        isError: false,
        isSystemMessage: true
      };
      
      setMessages((prevMessages) => [...prevMessages, restrictionMessage]);
      scrollToBottom(true);
      return;
    }

    // Check media usage limits for picture/video requests
    const detectedMediaType = detectMediaTypeFromMessage(input.trim());
    if (detectedMediaType !== "none" && user) {
      try {
        const usageCheck = await checkMediaUsageLimits(user, detectedMediaType);
        
        if (!usageCheck.canRequest) {
          console.log('Media request blocked due to usage limits:', usageCheck);
          
          // Create user-friendly error message
          const upgradeInfo = getUpgradeMessage(
            usageCheck.limitInfo.subscription_tier || "0", 
            detectedMediaType
          );
          
          const limitMessage = {
            id: `system_${Date.now()}`,
            type: "ai",
            msg: `I'd love to share a ${detectedMediaType} with you, but you've reached your monthly limit! ${upgradeInfo.message}`,
            isError: true,
            isSystemMessage: true,
            upgradeAction: upgradeInfo
          };
          
          setMessages((prevMessages) => [...prevMessages, limitMessage]);
          scrollToBottom(true);
          
          // Show upgrade dialog
          setUsageLimitDialog({
            mediaType: detectedMediaType,
            limitInfo: usageCheck.limitInfo,
            upgradeInfo: upgradeInfo
          });
          
          return;
        }
      } catch (error) {
        console.error('Error checking media usage limits:', error);
        // Continue with the request if usage check fails
      }
    }

    const fileInfo = attachedFile
      ? `\n[File: ${attachedFile.name}](${attachedFile.fullPath})`
      : "";
    const combinedInput = `${input.trim()}${fileInfo}`;

    const messageId = `human_${Date.now()}`;
    const userMessage = {
      id: messageId,
      type: "human",
      msg: combinedInput,
    };

    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setIsTyping(true);
    scrollToBottom(true); // Force scroll when user sends a message

    // Ensure we have a thread ID
    if (!currentThreadId) {
      console.error("No thread ID available for sending message");
      setIsTyping(false);
      return;
    }

    // Note: Using 'message' instead of 'human_input' to match middleware expectations
    const data = {
      human_input: combinedInput, // Keep this for our internal code
      message: combinedInput, // Add this for the middleware
      file_url: attachedFile?.fullPath || null,
      file_type: attachedFile?.type || null,
      bot_id: chatBotId,
      user_id: userId,
      initial_message_type: "text",
      thread_id: currentThreadId,
    };

    try {
      if (wsManager && wsManager.isConnected) {
        console.log("Sending message via WebSocket:", data);
        wsManager.sendMessage(data);

        // Clear any existing timeout
        if (responseTimeoutId) {
          clearTimeout(responseTimeoutId);
        }

        // Set a timeout to check if we've received a response
        const timeoutId = setTimeout(() => {
          if (isTyping) {
            console.warn("No response received within timeout period");
            setIsTyping(false);
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                id: `ai_timeout_${Date.now()}`,
                type: "ai",
                msg: "I'm sorry, I didn't receive a response in time. Please try again.",
              },
            ]);
            scrollToBottom(true); // Force scroll for timeout message
            setResponseTimeoutId(null);
          }
        }, 60000); // 60 second timeout - increased from 30 seconds

        // Store the timeout ID so we can cancel it if needed
        setResponseTimeoutId(timeoutId);
      } else {
        console.error("WebSocket is not connected. Message could not be sent.");
        setIsTyping(false);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            id: `ai_error_${Date.now()}`,
            type: "ai",
            msg: "Error: Could not connect to the server. Please refresh the page and try again.",
          },
        ]);
        scrollToBottom(true); // Force scroll for error message
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setIsTyping(false);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: `ai_error_${Date.now()}`,
          type: "ai",
          msg: `Error: ${error.message || "An unexpected error occurred. Please try again."}`,
        },
      ]);
      scrollToBottom(true); // Force scroll for error message
    }

    setInput("");
    setAttachedFile(null);
  };

  const handleFileUpload = async (fileInfo) => {
    setAttachedFile(fileInfo);
  };

  const handleAudioUpload = async (audioInfo) => {
    const messageId = `human_${Date.now()}`;
    const messageText = `[File: ${audioInfo.name}](${audioInfo.fullPath})[duration: ${audioInfo.duration}]`;
    const userMessage = {
      id: messageId,
      type: "human",
      msg: messageText,
    };

    setMessages((prevMessages) => [...prevMessages, userMessage]);
    scrollToBottom(true); // Force scroll for audio message
    setIsTyping(true);
    // Ensure we have a thread ID
    if (!currentThreadId) {
      console.error("No thread ID available for sending audio message");
      setIsTyping(false);
      return;
    }

    // Note: Using 'message' instead of 'human_input' to match middleware expectations
    const data = {
      human_input: messageText, // Keep this for our internal code
      message: messageText, // Add this for the middleware
      file_recorded_audio: audioInfo.fullPath,
      file_type: audioInfo.type,
      bot_id: chatBotId,
      user_id: userId,
      initial_message_type: "audio",
      thread_id: currentThreadId,
    };

    try {
      if (wsManager && wsManager.isConnected) {
        console.log("Sending audio message via WebSocket:", data);
        wsManager.sendMessage(data);
      } else {
        console.error("WebSocket is not connected. Message could not be sent.");
      }
    } catch (error) {
      console.error("Error sending audio message:", error);
    }
  };

  return (
    <div
      className="sm:col-span-12 flex flex-col bg-gray-900 text-white w-full min-h-screen bg-fixed bg-center bg-cover pt-20 z-30"
      style={{
        backgroundImage:
          chatBotId === "1"
            ? `url('https://publicsamassets.s3.amazonaws.com/img/sam_ai_sapiens.jpg')`
            : "none",
      }}
    >
      <div className="flex-grow flex flex-col mx-2 my-2 w-full relative">
        {isLoading ? (
          <div className="flex-grow flex items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex-grow relative">
            {!currentThreadId ? (
              <div className="flex flex-col justify-center items-center h-[calc(100vh-180px)] text-center px-4">
                <div className="bg-gray-800 p-6 rounded-lg shadow-lg max-w-md">
                  <h2 className="text-xl font-semibold mb-4 text-white">
                    Welcome to a new conversation
                  </h2>
                  <p className="text-gray-300 mb-6">
                    This is a new thread, but the AI remembers your past
                    conversations. Create a new thread to start chatting.
                  </p>
                  <button
                    onClick={async () => {
                      try {
                        // Import the createThread function
                        const { createThread } = await import(
                          "../utils/threadUtils"
                        );
                        const result = await createThread(userId, chatBotId);
                        console.log(`Thread creation result:`, result);

                        if (result && result.thread_id) {
                          console.log(
                            `Setting currentThreadId to ${result.thread_id}`
                          );
                          setCurrentThreadId(result.thread_id);
                          // Save this as the last visited thread
                          if (typeof localStorage !== "undefined") {
                            localStorage.setItem(
                              "lastVisitedThread",
                              JSON.stringify({
                                botId: chatBotId,
                                threadId: result.thread_id,
                              })
                            );
                          }
                        }
                      } catch (error) {
                        console.error("Error creating thread:", error);
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center mx-auto"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Create New Thread
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="overflow-y-auto h-[calc(100vh-180px)] mb-32"
                onScroll={(e) => {
                  // Check if user is scrolling up
                  const { scrollTop, scrollHeight, clientHeight } =
                    e.currentTarget;
                  // Only consider it scrolling up if we're more than 100px from the bottom
                  const distanceFromBottom =
                    scrollHeight - scrollTop - clientHeight;
                  const isScrollingUp = distanceFromBottom > 100;

                  // Set userScrolling state
                  if (isScrollingUp && !userScrolling) {
                    setUserScrolling(true);
                    console.log(
                      "User started scrolling up, distance from bottom:",
                      distanceFromBottom
                    );
                  } else if (!isScrollingUp && userScrolling) {
                    // Clear any existing timeout
                    if (scrollTimeoutRef.current) {
                      clearTimeout(scrollTimeoutRef.current);
                    }

                    // Set a timeout to reset userScrolling after user stops scrolling
                    // Only if they're close to the bottom
                    if (distanceFromBottom < 50) {
                      scrollTimeoutRef.current = setTimeout(() => {
                        setUserScrolling(false);
                        console.log(
                          "User stopped scrolling near bottom, auto-scroll enabled"
                        );
                      }, 10000); // 10 seconds timeout - increased from 5 seconds
                    }
                  }
                }}
              >
                <MessageList
                  messages={messages}
                  messagesEndRef={messagesEndRef}
                  chatBotId={chatBotId}
                  isTyping={isTyping}
                  userCharacters={userCharacters}
                />
              </div>
            )}
            {currentThreadId && userScrolling && (
              <button
                onClick={() => {
                  setUserScrolling(false);
                  setTimeout(() => scrollToBottom(true), 100);
                }}
                className="fixed bottom-24 right-4 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full shadow-lg z-50"
                aria-label="Scroll to bottom"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 bg-opacity-95 p-4">
          <div className="max-w-screen-xl mx-auto">
            <div className="flex flex-col gap-2">
              {attachedFile && (
                <FileAttachmentPreview
                  file={attachedFile}
                  onRemove={() => setAttachedFile(null)}
                />
              )}
              {currentThreadId && (
                <SuggestionPills onSuggestionClick={handleSuggestionClick} />
              )}
              <MessageInput
                input={input}
                handleInputChange={handleInputChange}
                handleKeyPress={handleKeyPress}
                handlePostRequest={handlePostRequest}
                handleFileUpload={handleFileUpload}
                handleAudioUpload={handleAudioUpload}
                attachedFile={attachedFile}
                setAttachedFile={setAttachedFile}
                userId={userId}
                chatBotId={chatBotId}
                disabled={!currentThreadId || (subscriptionStatus && !subscriptionStatus.canSendMessages)}
                placeholder={
                  !currentThreadId
                    ? "Create a thread to start chatting"
                    : subscriptionStatus && !subscriptionStatus.canSendMessages
                      ? subscriptionStatus.message
                      : "Type your message here..."
                }
                subscriptionStatus={subscriptionStatus}
              />
              
              {/* Subscription Status Banner */}
              {subscriptionStatus && !subscriptionStatus.canSendMessages && subscriptionStatus.type === 'subscription_required' && (
                <div className="bg-blue-900 border border-blue-700 rounded-lg p-3 mt-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-blue-200 text-sm">
                        {subscriptionStatus.message}
                      </span>
                    </div>
                    <a
                      href="/subscription-management"
                      className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1 rounded transition-colors"
                    >
                      Subscribe
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Usage Indicator */}
        {user && currentThreadId && (
          <UsageIndicator
            position="top-right"
            show={showUsageIndicator}
            currentMessage={input}
            onLimitExceeded={(mediaType, limitInfo) => {
              const upgradeInfo = getUpgradeMessage(limitInfo.subscriptionTier, mediaType);
              setUsageLimitDialog({
                mediaType,
                limitInfo,
                upgradeInfo
              });
            }}
          />
        )}
        
        {/* Usage Limit Dialog */}
        {usageLimitDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="text-center">
                <div className="text-4xl mb-4">🚫</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Monthly {usageLimitDialog.mediaType} limit reached
                </h3>
                <p className="text-gray-600 mb-4">
                  You've used all your {usageLimitDialog.mediaType} requests for this month.
                  {usageLimitDialog.limitInfo.current_count && usageLimitDialog.limitInfo.limit && (
                    <span className="block text-sm mt-1">
                      ({usageLimitDialog.limitInfo.current_count}/{usageLimitDialog.limitInfo.limit} used)
                    </span>
                  )}
                </p>
                <p className="text-blue-600 font-medium mb-6">
                  {usageLimitDialog.upgradeInfo.message}
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setUsageLimitDialog(null)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setUsageLimitDialog(null);
                      window.location.href = '/subscription-management';
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {usageLimitDialog.upgradeInfo.buttonText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatBox;
